import { isDefined, isEmpty } from '../isEmpty'
import { isNotDeleted } from '../common'
import {
  type AttachmentCustomFieldData,
  type CalculationCustomFieldData,
  type CalculationOperation,
  CUSTOM_FIELD_CALCULATION_OPERATIONS,
  CUSTOM_FIELD_TYPE_NAMES,
  type CustomFieldData,
  type CustomFieldFilter,
  type CustomFieldFormat,
  CustomFieldFormatSchema,
  type CustomFieldId,
  type CustomFieldMap,
  type CustomFieldSearchResult,
  type CustomFieldType,
  type CustomFieldTypeName,
  isCustomFieldId,
  type MultipleChoiceCustomFieldData,
  type RequiredAccountField,
} from '../../model'

export function isCalculationCustomField(
  customField?: Pick<CustomFieldData, 'fieldType'>
): customField is CalculationCustomFieldData {
  return customField?.fieldType === 'calculation'
}

export function isMultipleChoiceCustomField(
  customField?: Pick<CustomFieldData, 'fieldType'>
): customField is MultipleChoiceCustomFieldData {
  return customField?.fieldType === 'multipleChoice'
}

export function isAttachmentCustomField(
  customField?: Pick<CustomFieldData, 'fieldType'>
): customField is AttachmentCustomFieldData {
  return customField?.fieldType === 'attachment'
}

export function isCustomFieldTypeName(value: unknown): value is CustomFieldTypeName {
  if (typeof value !== 'string') return false
  return CUSTOM_FIELD_TYPE_NAMES.some((name) => name === value)
}

export const getMissingCustomFields = (
  customFieldValues: CustomFieldMap | undefined,
  requiredFields: RequiredAccountField[],
  customFields: CustomFieldData[]
): CustomFieldId[] => {
  const validRequiredCustomFields: CustomFieldId[] = requiredFields.filter((field): field is CustomFieldId => {
    if (!isCustomFieldId(field)) return false
    const customField = customFields.find((cf) => cf.fieldId === field)
    return isNotDeleted(customField)
  })
  if (isEmpty(customFieldValues)) return validRequiredCustomFields
  return validRequiredCustomFields.filter((field) => isEmpty(customFieldValues[field]))
}

export function isCalculationOperation(operation: unknown): operation is CalculationOperation {
  if (typeof operation !== 'string') return false
  return CUSTOM_FIELD_CALCULATION_OPERATIONS.some((op) => op === operation)
}

export function canBeUsedInCalculation(customField: Pick<CustomFieldData, 'fieldType'>): boolean {
  return customField.fieldType === 'number' || customField.fieldType === 'calculation'
}

export function sortCustomFields<T extends Pick<CustomFieldData, 'order' | 'label'>>(customFields: T[]): T[] {
  return customFields.sort((a, b) => {
    if (isDefined(a.order) && isDefined(b.order)) return a.order - b.order
    if (isDefined(a.order)) return -1
    if (isDefined(b.order)) return 1
    return a.label.localeCompare(b.label)
  })
}

export function isCustomFieldFormat(format: unknown): format is CustomFieldFormat {
  return CustomFieldFormatSchema.safeParse(format).success
}

export function toAdvancedCustomFields(
  customFields: CustomFieldSearchResult[] | undefined
): CustomFieldFilter | undefined {
  if (isEmpty(customFields)) return undefined

  const grouped = customFields.reduce((acc, { id, value }) => {
    const existing = acc.get(id) ?? []
    existing.push(value)
    acc.set(id, existing)
    return acc
  }, new Map<CustomFieldId, CustomFieldType[]>())

  const filter: CustomFieldFilter = {}
  for (const [id, values] of grouped) {
    filter[id] = { type: 'multipleChoice', values }
  }
  return isEmpty(filter) ? undefined : filter
}
